"use client";

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useGame } from '@/app/providers/GameContextProvider';
import { GameHazard } from '@/lib/types/common/GameMode';
import { GameStep } from '@/lib/types/common/GameStep';
import { motion } from 'framer-motion';
import { IoTimeOutline } from 'react-icons/io5';
import IconGlowWrapper from '@/app/components/IconGlowWrapper';
import { useGameQuality } from '@/app/providers/GameQualityProvider';
import { getHazardDefinition } from '@/lib/config/hazardConfig';
import { useAudio, AUDIO } from '@/app/providers/AudioProvider';

// Helper function to get timer multiplier from hazards
function getHazardTimerMultiplier(hazards: string[]): number {
    let multiplier = 1.0;

    hazards.forEach(hazard => {
        const hazardDef = getHazardDefinition(hazard as GameHazard);
        if (hazardDef?.timerMultiplier) {
            multiplier *= hazardDef.timerMultiplier;
        }
    });

    return multiplier;
}

const RoundTimer = () => {
    const { gameState } = useGame();
    const { getBackdropBlurIntensity } = useGameQuality();
    const audio = useAudio();
    // Initialize with timer seconds from game settings
    const initialTimerSeconds = gameState?.gameSettings?.timerSeconds || 60;
    const [remainingTime, setRemainingTime] = useState<number>(initialTimerSeconds);
    const [localTimerStart, setLocalTimerStart] = useState<number | null>(null);
    const hideMessageTimeout = useRef<NodeJS.Timeout | null>(null);
    const lastTickSecond = useRef<number | null>(null);

    // Calculate timer multiplier from current hazards
    const timerMultiplier = useMemo(() => {
        if (!gameState?.currentHazards) return 1.0;
        return getHazardTimerMultiplier(Array.from(gameState.currentHazards));
    }, [gameState?.currentHazards]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (hideMessageTimeout.current) {
                clearTimeout(hideMessageTimeout.current);
            }
        };
    }, []);

    // Reset timer to full when round changes or when entering guessing phase
    useEffect(() => {
        const timerSeconds = gameState?.gameSettings?.timerSeconds || 60;
        setRemainingTime(timerSeconds);
        setLocalTimerStart(null);
        lastTickSecond.current = null; // Reset tick tracker on round change
    }, [gameState?.currentRound, gameState?.gameSettings?.timerSeconds]);

    // Start local timer when roundStartTime is set by the server
    // The server sets roundStartTime AFTER the countdown completes, so this component
    // (which only renders after countdown) will be in sync
    useEffect(() => {
        if (gameState?.gameStep === GameStep.GUESSING && gameState?.roundStartTime && gameState.roundStartTime > 0) {
            // Use server's roundStartTime directly - it's set after countdown completes
            if (!localTimerStart) {
                setLocalTimerStart(gameState.roundStartTime);
            }
        } else if (gameState?.gameStep !== GameStep.GUESSING) {
            setLocalTimerStart(null);
        }
    }, [gameState?.gameStep, gameState?.roundStartTime, localTimerStart]);


    useEffect(() => {
        // Timer seconds from settings
        const baseTimerSeconds = gameState?.gameSettings?.timerSeconds || 0;

        if (!gameState || !baseTimerSeconds || baseTimerSeconds <= 0) {
            return;
        }

        // Don't start counting down until we have a valid local start time (set from server's roundStartTime)
        if (!localTimerStart) {
            return;
        }

        const updateTimer = () => {
            const now = Date.now();
            // Use local timer start for countdown
            const elapsedMs = now - localTimerStart;

            // Apply timer multiplier to elapsed time as well for visual effect
            const effectiveElapsedSeconds = (elapsedMs / 1000) * timerMultiplier;
            const remaining = Math.max(0, baseTimerSeconds - effectiveElapsedSeconds);
            setRemainingTime(Math.floor(remaining));
        };

        updateTimer();
        // Update more frequently when timer is accelerated
        const updateInterval = timerMultiplier > 1 ? 500 : 1000;
        const interval = setInterval(updateTimer, updateInterval);

        return () => clearInterval(interval);
    }, [gameState?.gameSettings?.timerSeconds, gameState?.allPlayersLoadedPhotosphere, localTimerStart, timerMultiplier]);

    // Play tick sound during last 10 seconds
    useEffect(() => {
        // Only play tick if timer is active and we're in the last 10 seconds
        if (remainingTime > 0 && remainingTime <= 10) {
            // Only play if this second is different from the last tick
            if (lastTickSecond.current !== remainingTime) {
                lastTickSecond.current = remainingTime;
                audio.play(AUDIO.TICK);
            }
        }
    }, [remainingTime, audio]);

    // Format time as MM:SS
    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const isUrgent = remainingTime <= 20;
    const isCritical = remainingTime <= 10;

    if (remainingTime < 0) {
        return null;
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: -20, scale: 1 }}
            animate={{ opacity: 1, y: 0, scale: isCritical ? 1.5 : 1 }}
            transition={{ duration: 0.3 }}
            className="fixed top-[20%] right-10 origin-top z-50 w-36 justify-center pointer-events-none select-none"
        >
            <div className={`absolute top-0 left-0 w-full h-full rounded-full blur-sm ${getBackdropBlurIntensity('sm')} z-[-1]`}></div>
            <div className="flex items-center justify-center gap-3">
                <IconGlowWrapper
                    icon={<IoTimeOutline className="text-3xl" />}
                    color={isCritical ? 'red' : isUrgent ? 'yellow' : 'blue'}
                />
                <span className={`
                                        text-3xl font-bold font-orgon-plan
                                        text-shadow
                                        ${isCritical ? 'text-red-100 shadow-red-600' : isUrgent ? 'text-yellow-100 shadow-yellow-600' : 'text-white shadow-blue-600'}
                                    `}>
                    {formatTime(remainingTime)}
                </span>
            </div>
        </motion.div>
    );
};

export default RoundTimer;